import React, { useEffect, useMemo, useState } from 'react';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, ReferenceLine,
} from 'recharts';
import MeasuredBox from 'shared/components/ui/MeasuredBox';
import { tableHeaderRowClass, tableHeaderCellClass, tableDataCellClass, tableRowClass } from 'features/activity/styles/dataTableStyles';
import { currency, compact, duration } from 'features/evals3/numberFormat';
import { getTelemetry, getSla, getStats, getSteps, compareVersions } from 'features/evals3/api/evals3Api';
import { kindMeta } from 'features/evals3/editor/nodeKinds';
import { statesByDay, seamDays, passRateByVersion, pct } from 'features/evals3/pages/telemetryModel';
import { viewerTz, hourKeyAt, clusterFor, bucketLabel } from 'features/evals3/pages/timeBuckets';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import { NAMED_PALETTE } from 'features/agent-analytics/components/failures/FailuresSummary';
import { PageLoader } from 'shared/components/ui/BrandLoader';

/**
 * What an eval has been deciding, as a few numbers and charts (TRO-430).
 *
 * Drawn the way the Traces page draws: no chart fill, no gridlines, no
 * y-axis (the hover carries the numbers), colours from theme tokens, sized
 * with MeasuredBox — never ResponsiveContainer, which collapses charts in
 * this app (see MeasuredBox).
 *
 * Titles only; what a title needs said is on its hover. A card with nothing
 * to show is left out, not explained.
 *
 * SCOPE. The page's Filter picks the period, the version and the steps. With
 * steps chosen, every number is about the runs whose path went through all
 * of them, and each chosen step gets its own card. Picking a path or a step
 * in the tables narrows the scope; picking a version row narrows the version.
 */

const TOKEN = {
  brand: 'var(--brand-primary)',
  success: 'var(--feedback-success)',
  error: 'var(--feedback-error)',
  warning: 'var(--feedback-warning)',
  info: 'var(--feedback-info)',
  teal: 'var(--accent-enterprise, #0d9488)',
};
const STATES = [
  { key: 'passed', label: 'Passed', color: TOKEN.success },
  { key: 'failed', label: 'Failed', color: TOKEN.error },
  { key: 'errored', label: 'Errored', color: TOKEN.warning },
  { key: 'pending', label: 'Waiting', color: TOKEN.info },
];
const outcomeTone = (passed) => (passed === false ? TOKEN.error : passed === true ? TOKEN.success : TOKEN.brand);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** '2026-09-21' → '21 Sep', read as a calendar date, not shifted by the viewer's timezone. */
export function dayLabel(day) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(day || ''));
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}` : String(day || '');
}

// ---------------------------------------------------------------------------
// Parts
// ---------------------------------------------------------------------------

const axisTick = { fontSize: 10, fill: 'var(--text-muted)' };
const readRow = 'h-8 border-b border-[color:var(--border-subtle)] last:border-b-0';

/** A thin bordered card: a title (its explanation on hover), optional controls, the content. */
function Card({ title, info, controls, children, className = '', testId }) {
  return (
    <section className={`flex flex-col rounded-lg border border-[color:var(--border-default)] ${className}`} data-testid={testId} aria-label={title}>
      <header className="flex min-h-[36px] shrink-0 items-center justify-between gap-2 px-3 pt-2.5">
        <h3 title={info || undefined}
          className={`truncate text-[12px] font-medium text-[color:var(--text-primary)] ${info ? 'cursor-help' : ''}`}>
          {title}
        </h3>
        {controls ? <div className="flex shrink-0 items-center gap-3">{controls}</div> : null}
      </header>
      <div className="min-h-0 flex-1 px-3 pb-3 pt-1">{children}</div>
    </section>
  );
}

/** "Cluster by": hour, 6 hours, 12 hours or day, on the viewer's clock. Opens on the window's choice. */
function ClusterBy({ title, cluster, onChange }) {
  if (!cluster || cluster.offered.length < 2) return null;
  return (
    <div className="w-[128px]">
      <BrandDropdown compact ariaLabel={`${title}: cluster by`} value={cluster.size} onChange={onChange}
        options={cluster.offered.map((o) => ({ value: o.value, label: o.label }))} />
    </div>
  );
}

/** A chart at a fixed height, measured rather than responsive. `summary` is what a screen reader hears. */
function Chart({ height = 208, summary, children }) {
  return (
    <div style={{ height }} role="img" aria-label={summary}>
      <MeasuredBox>{({ width, height: h }) => children({ width, height: h })}</MeasuredBox>
    </div>
  );
}

/** The hover: a raised card with the bucket's label and one swatch row per series. */
function HoverCard({ active, payload, rows }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const lines = rows(row).filter(Boolean);
  if (!lines.length) return null;
  return (
    <div className="rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] px-3 py-2 text-[11px] shadow-lg">
      <div className="mb-1 text-[color:var(--text-muted)]">{row.label}</div>
      {lines.map((l) => (
        <div key={l.name} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5 text-[color:var(--text-secondary)]">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: l.color }} />{l.name}
          </span>
          <span className="tabular-nums text-[color:var(--text-primary)]">{l.value}</span>
        </div>
      ))}
    </div>
  );
}
const hoverProps = { cursor: { fill: 'var(--surface-hover)', opacity: 0.5 }, isAnimationActive: false, wrapperStyle: { outline: 'none' } };

/** A score, at the precision its own scale deserves: 3.5 of 5 wants one decimal, 0.91 two, a whole 4 stays 4. */
function scoreFmt(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  const places = Math.abs(v) < 1 ? 2 : 1;
  return String(Number(v.toFixed(places)));
}

/** One number with, at most, a tiny line under it. `hover` is the full sentence when the line is cut short. */
function Tile({ label, value, sub, hover, tone }) {
  const color = tone === 'good' ? 'text-success' : tone === 'bad' ? 'text-danger' : tone === 'warn' ? 'text-warning' : 'text-[color:var(--text-primary)]';
  return (
    <div className="rounded-lg border border-[color:var(--border-default)] px-3 py-2.5">
      <dt className="truncate text-[12px] text-[color:var(--text-secondary)]">{label}</dt>
      <dd className={`mt-0.5 truncate text-[24px] font-semibold leading-tight tabular-nums ${color}`}>{value}</dd>
      {sub ? <dd className="mt-0.5 truncate text-[11px] text-[color:var(--text-muted)]" title={hover || (typeof sub === 'string' ? sub : undefined)}>{sub}</dd> : null}
    </div>
  );
}

/** A horizontal bar row: label left, a thin bar, the figure right. */
function BarRow({ label, title, value, fraction, color, figure, outcome }) {
  return (
    <li className="text-[12px]" title={title}>
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate text-[color:var(--text-primary)]">{label}</span>
        <span className="shrink-0 tabular-nums text-[color:var(--text-secondary)]">{figure ?? value}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full" aria-hidden>
        <div className="h-full rounded-full" data-outcome={outcome}
          style={{ width: `${Math.max(1, Math.round(Math.min(1, fraction || 0) * 100))}%`, background: color }} />
      </div>
    </li>
  );
}

/** The palette's name for what ran: an instrument read as a node, so both semantic instruments say Semantic check. */
const KIND_LABEL = (instrument) => kindMeta({ type: 'guard', instrument }).label;

/** A share bar coloured by how its runs ended. */
function EndedBar({ passed = 0, failed = 0, errored = 0, pending = 0 }) {
  const total = passed + failed + errored + pending;
  if (!total) return null;
  const seg = (n, color, key) => (n ? <div key={key} style={{ width: `${(n / total) * 100}%`, background: color }} /> : null);
  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded-full" aria-hidden>
      {seg(passed, TOKEN.success, 'p')}{seg(failed, TOKEN.error, 'f')}{seg(errored, TOKEN.warning, 'e')}{seg(pending, TOKEN.info, 'w')}
    </div>
  );
}

const th = (extra = '') => `${tableHeaderCellClass} ${extra}`;

// ---------------------------------------------------------------------------
// Paths, steps, and a card per chosen step
// ---------------------------------------------------------------------------

function PathsCard({ paths, runs, onPick }) {
  return (
    <Card title="Paths" info="Every walk the eval took, entry to verdict. Pick one to see the page for just those runs." testId="card-paths">
      <table className="w-full table-fixed text-left text-[12px]" data-testid="eval3-paths">
        <thead>
          <tr className={tableHeaderRowClass}>
            <th scope="col" className={th()}>Path</th>
            <th scope="col" className={th('w-[92px] text-right')}>Runs</th>
            <th scope="col" className={th('w-[20%]')}>Ended</th>
            <th scope="col" className={th('w-[64px] text-right')}>Pass</th>
          </tr>
        </thead>
        <tbody>
          {paths.map((p) => (
            <tr key={p.path.join('>')} className={tableRowClass} onClick={() => onPick(p.path.slice(0, -1))}
              title="See only the runs that took this path">
              <td className={`${tableDataCellClass} max-w-0`}>
                <span className="block truncate" title={p.names.join(' → ')}>
                  {p.names.map((n, i) => (
                    <span key={`${n}-${i}`}>{i > 0 && <span className="px-1 text-[color:var(--text-muted)]">→</span>}
                      <span className={i === p.names.length - 1 ? 'font-medium' : ''}>{n}</span></span>
                  ))}
                </span>
              </td>
              <td className={`${tableDataCellClass} whitespace-nowrap text-right tabular-nums`}>{compact(p.runs)} <span className="text-[color:var(--text-muted)]">{runs ? pct(p.share) : ''}</span></td>
              <td className={tableDataCellClass}><EndedBar {...p} /></td>
              <td className={`${tableDataCellClass} text-right tabular-nums`}>{p.pass_rate == null ? '—' : pct(p.pass_rate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function StepsCard({ steps, chosen, onToggle }) {
  return (
    <Card title="Steps" info="Every step that ran. Pick one to add it to the scope and open its own card." testId="card-steps">
      <table className="w-full table-fixed text-left text-[12px]" data-testid="eval3-steps">
        <thead>
          <tr className={tableHeaderRowClass}>
            <th scope="col" className={th()}>Step</th>
            <th scope="col" className={th('w-[56px] text-right')}>Runs</th>
            <th scope="col" className={th('w-[56px] text-right')}>Errors</th>
            <th scope="col" className={th('w-[60px] text-right')}>p50</th>
            <th scope="col" className={th('w-[72px] text-right')}>Spend</th>
          </tr>
        </thead>
        <tbody>
          {steps.map((st) => {
            const on = chosen.includes(st.node_id);
            return (
              <tr key={st.node_id} className={`${tableRowClass} ${on ? 'bg-surface-hover' : ''}`} onClick={() => onToggle(st.node_id)} aria-selected={on}>
                <td className={`${tableDataCellClass} max-w-0`}>
                  <span className="block truncate"><span className="font-medium">{st.name}</span> <span className="text-[11px] text-[color:var(--text-muted)]">{KIND_LABEL(st.instrument)}</span></span>
                </td>
                <td className={`${tableDataCellClass} text-right tabular-nums`}>{compact(st.runs)}</td>
                <td className={`${tableDataCellClass} text-right tabular-nums ${st.errors ? 'text-warning' : ''}`}>{st.errors || '—'}</td>
                <td className={`${tableDataCellClass} text-right tabular-nums`}>{st.latency_ms.p50 == null ? '—' : duration(st.latency_ms.p50)}</td>
                <td className={`${tableDataCellClass} text-right tabular-nums`}>{st.provider_cost_usd ? currency(st.provider_cost_usd) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

/** What a step (or one of its fields) answered, and how the runs with each answer ended. */
function AnswersTable({ outcomes, label, head = 'Answered' }) {
  return (
    <table className="w-full table-fixed text-left text-[12px]" aria-label={label}>
      <thead>
        <tr className={tableHeaderRowClass}>
          <th scope="col" className={th()}>{head}</th>
          <th scope="col" className={th('w-[92px] text-right')}>Runs</th>
          <th scope="col" className={th('w-[30%]')}>Ended</th>
          <th scope="col" className={th('w-[56px] text-right')}>Pass</th>
        </tr>
      </thead>
      <tbody>
        {outcomes.map((o) => (
          <tr key={o.label} className={readRow}>
            <td className={`${tableDataCellClass} max-w-0 truncate ${o.other ? 'text-[color:var(--text-muted)]' : ''}`} title={o.label}>{o.label}</td>
            <td className={`${tableDataCellClass} whitespace-nowrap text-right tabular-nums`}>{compact(o.runs)} <span className="text-[color:var(--text-muted)]">{pct(o.share)}</span></td>
            <td className={tableDataCellClass}><EndedBar {...o} /></td>
            <td className={`${tableDataCellClass} text-right tabular-nums`}>{o.pass_rate == null ? '—' : pct(o.pass_rate)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** How numbers spread over their range, each bin split by how its runs ended. */
function Histogram({ dist, summary, height = 120 }) {
  return (
    <Chart height={height} summary={summary}>
      {({ width, height: h }) => (
        <ComposedChart width={width} height={h} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}
          data={dist.bins.map((b) => ({ ...b, label: dist.whole ? String(b.from) : `${scoreFmt(b.from)}–${scoreFmt(b.to)}` }))}>
          <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: 'var(--border-default)' }} tickLine={false} minTickGap={4} />
          <YAxis hide />
          <Tooltip {...hoverProps} content={(p) => <HoverCard {...p} rows={(r) => [
            r.passed ? { name: 'Passed', color: TOKEN.success, value: r.passed } : null,
            r.failed ? { name: 'Failed', color: TOKEN.error, value: r.failed } : null,
            r.runs > r.passed + r.failed ? { name: 'Other', color: TOKEN.info, value: r.runs - r.passed - r.failed } : null,
          ]} />} />
          <Bar dataKey="passed" name="Passed" stackId="d" fill={TOKEN.success} maxBarSize={28} isAnimationActive={false} />
          <Bar dataKey="failed" name="Failed" stackId="d" fill={TOKEN.error} maxBarSize={28} radius={[2, 2, 0, 0]} isAnimationActive={false} />
        </ComposedChart>
      )}
    </Chart>
  );
}

/** One field of a step that answers several: a number as its spread, anything else as its answers. */
function FieldBlock({ step, field }) {
  const title = (
    <div className="mb-1 flex items-baseline justify-between gap-3 text-[12px]">
      <span className="truncate font-medium text-[color:var(--text-primary)]">{field.key}</span>
      <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]">
        {field.type === 'number' ? `mean ${scoreFmt(field.mean)}` : `${compact(field.distinct)} ${field.distinct === 1 ? 'answer' : 'answers'}`}
      </span>
    </div>
  );
  return (
    <div className="min-w-0" data-testid={`step-field-${field.key}`}>
      {title}
      {field.type === 'number' && field.distribution
        ? <Histogram dist={field.distribution} summary={`${step.name}, ${field.key}: from ${field.distribution.min} to ${field.distribution.max}, mean ${field.mean}`} />
        : <AnswersTable outcomes={field.outcomes || []} label={`${step.name}: ${field.key}`} head={field.key} />}
    </div>
  );
}

/** One chosen step: what it answered and how those runs ended, where it routed, and how its scores spread. */
function StepCard({ step, onRemove }) {
  const facts = [
    `${compact(step.calls)} ${step.calls === 1 ? 'run' : 'runs'}`,
    step.latency_ms.p50 != null ? `p50 ${duration(step.latency_ms.p50)} · p95 ${duration(step.latency_ms.p95)}` : null,
    step.errors ? `${step.errors} errored (${pct(step.error_rate)})` : null,
    step.provider_cost_usd ? `${currency(step.provider_cost_usd)} spend` : null,
    step.tokens_in ? `${compact(step.tokens_in + step.tokens_out)} tokens` : null,
    step.mean != null ? `mean ${step.mean}` : null,
  ].filter(Boolean);
  const dist = step.distribution;
  const fields = step.fields || [];
  const routes = step.routes.length > 0 ? (
    <ul className="space-y-2" aria-label={`${step.name}: routes`}>
      {step.routes.map((r) => (
        <BarRow key={r.to} fraction={r.share} color={outcomeTone(r.passed)}
          label={<><span className="text-[color:var(--text-muted)]">→</span> {r.name}{r.verdict ? <span className="text-[11px] text-[color:var(--text-muted)]"> · verdict</span> : null}</>}
          figure={<>{pct(r.share)} <span className="text-[color:var(--text-muted)]">· {compact(r.runs)}</span></>} />
      ))}
    </ul>
  ) : null;
  return (
    <Card title={step.name} testId={`card-step-${step.node_id}`} className="xl:col-span-2"
      controls={(
        <>
          <span className="text-[11px] text-[color:var(--text-muted)]">{KIND_LABEL(step.instrument)}</span>
          {onRemove && (
            <button type="button" onClick={onRemove} aria-label={`Remove ${step.name} from the scope`}
              className="rounded px-1.5 text-[11px] text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]">Remove</button>
          )}
        </>
      )}>
      <p className="pb-2 text-[11px] text-[color:var(--text-muted)]" data-testid="step-facts">{facts.join(' · ')}</p>
      {fields.length > 0 ? (
        // Several fields: each on its own, side by side, then where the step routed.
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {fields.map((f) => <FieldBlock key={f.key} step={step} field={f} />)}
          </div>
          {routes}
        </div>
      ) : (
        <div className={`grid grid-cols-1 gap-6 ${dist || step.routes.length > 0 ? 'lg:grid-cols-2' : ''}`}>
          <AnswersTable outcomes={step.outcomes} label={`${step.name}: answers`} />
          <div className="min-w-0 space-y-4">
            {routes}
            {dist && (
              <div data-testid="step-distribution" title={step.mean != null ? `mean ${step.mean}` : undefined}>
                <Histogram dist={dist} summary={`${step.name}: score distribution from ${dist.min} to ${dist.max}`} />
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Versions
// ---------------------------------------------------------------------------

const points = (a, b) => {
  if (a == null || b == null) return null;
  const d = Math.round((a - b) * 1000) / 10;
  return `${d > 0 ? '+' : d < 0 ? '−' : ''}${Math.abs(d)} pts`;
};

/** Pass rate by version, as bars. Only worth a card when more than one version has verdicts. Picking one scopes the page to it. */
export function VersionsCard({ data, selected, onPick }) {
  const rows = (data?.versions || []).filter((v) => v.results > 0);
  if (rows.length < 2) return null;
  return (
    <Card title="Versions" info="Pass rate of each version over the same window. Pick one to see the page for it alone." testId="card-versions">
      <ul className="space-y-2.5" data-testid="eval3-versions">
        {rows.map((v) => {
          const on = selected === v.version;
          return (
            <li key={v.version}>
              <button type="button" onClick={() => onPick?.(on ? null : v.version)} aria-pressed={on}
                title={on ? 'Show every version' : `See the page for v${v.version} alone`}
                className={`block w-full rounded px-1 py-0.5 text-left text-[12px] hover:bg-surface-hover ${on ? 'bg-surface-hover' : ''}`}>
                <div className="mb-1 flex items-baseline justify-between gap-3">
                  <span className="font-medium tabular-nums text-[color:var(--text-primary)]">
                    v{v.version}{v.production ? <span className="ml-1.5 text-[11px] font-normal text-success">production</span> : null}
                  </span>
                  <span className="tabular-nums text-[color:var(--text-secondary)]">
                    {v.pass_rate == null ? '—' : pct(v.pass_rate)} <span className="text-[color:var(--text-muted)]">· {compact(v.resolved)}</span>
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full" aria-hidden>
                  <div className="h-full rounded-full" style={{ width: `${Math.max(1, Math.round((v.pass_rate || 0) * 100))}%`, background: TOKEN.brand }} />
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/** What a picked version is compared against: production, or when it IS production the newest older one with verdicts. */
export function baselineOf(versions, picked) {
  if (picked == null || !versions?.length) return null;
  const prod = versions.find((v) => v.production);
  if (prod && prod.version !== picked && prod.resolved) return prod;
  return versions.filter((v) => v.version < picked && v.resolved).sort((a, b) => b.version - a.version)[0] || null;
}

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
const versionColor = (i) => NAMED_PALETTE[i % NAMED_PALETTE.length];

/**
 * Every version side by side over the same window: what each decided, how
 * it scored, what it cost and how long it took, what people said about it,
 * its pass rate over time, and — the only like-for-like comparison — how
 * versions that scored the SAME units agreed.
 */
export function VersionsCompare({ compared, series, at, onPick }) {
  const all = compared?.versions || [];
  const rows = all.filter((v) => v.results > 0);
  const prod = rows.find((v) => v.production) || null;
  const anyScore = rows.some((v) => v.avg_score != null);
  const anyPeople = rows.some((v) => v.marked_right + v.marked_wrong > 0);
  const anyAudit = rows.some((v) => v.audited > 0);
  const order = [...rows].sort((a, b) => a.version - b.version);
  const colorOf = Object.fromEntries(order.map((v, i) => [v.version, versionColor(i)]));
  const lines = useMemo(() => {
    const { rows: r, keys } = passRateByVersion(series || [], at);
    return { rows: r.map((x) => ({ ...x, label: at ? bucketLabel(x.day, at.size) : dayLabel(x.day) })), keys };
  }, [series, at]);
  const shownKeys = lines.keys.filter((k) => lines.rows.some((r) => r[k] != null));
  const pairs = compared?.head_to_head || [];
  if (!rows.length) return <p className="p-4 text-[12px] text-[color:var(--text-muted)]">No version has results in this window.</p>;
  return (
    <div className="grid grid-cols-1 gap-3" data-testid="versions-compare">
      <Card title="Versions" info="Each version over the same window. Two versions live at different times score different traffic; the head-to-head below is the like-for-like view.">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] table-fixed text-left text-[12px]" aria-label="Versions compared">
            <thead>
              <tr className={tableHeaderRowClass}>
                <th scope="col" className={th('w-[150px]')}>Version</th>
                <th scope="col" className={th('text-right')}>Verdicts</th>
                <th scope="col" className={th('text-right')}>Pass rate</th>
                {prod && <th scope="col" className={th('text-right')}>vs v{prod.version}</th>}
                {anyScore && <th scope="col" className={th('text-right')}>Avg score</th>}
                <th scope="col" className={th('text-right')}>Errored</th>
                <th scope="col" className={th('text-right')}>p50 · p95</th>
                <th scope="col" className={th('text-right')}>Cost / verdict</th>
                {anyPeople && <th scope="col" className={th('text-right')} title="Verdicts people marked right and wrong">People agreed</th>}
                {anyAudit && <th scope="col" className={th('text-right')} title="Audited verdicts where the full path disagreed with the shortcut">Audit</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.version} className={tableRowClass} onClick={() => onPick?.(v.version)} title={v.change_note || `See the page for v${v.version} alone`}>
                  <td className={`${tableDataCellClass} max-w-0`}>
                    <span className="flex items-center gap-1.5 truncate">
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: colorOf[v.version] }} aria-hidden />
                      <span className="font-medium tabular-nums">v{v.version}</span>
                      {v.production ? <span className="text-[11px] text-[color:var(--text-muted)]">production</span> : null}
                    </span>
                    <span className="block truncate text-[11px] text-[color:var(--text-muted)]">{fmtDate(v.created_at)}{v.change_note ? ` · ${v.change_note}` : ''}</span>
                  </td>
                  <td className={`${tableDataCellClass} text-right tabular-nums`} title={v.rescored ? `${compact(v.rescored)} of them re-scored history` : undefined}>{compact(v.resolved)}</td>
                  <td className={`${tableDataCellClass} text-right tabular-nums`}>{pct(v.pass_rate)}</td>
                  {prod && <td className={`${tableDataCellClass} text-right tabular-nums text-[color:var(--text-secondary)]`}>{v.version === prod.version ? '—' : (points(v.pass_rate, prod.pass_rate) || '—')}</td>}
                  {anyScore && <td className={`${tableDataCellClass} text-right tabular-nums`}>{scoreFmt(v.avg_score)}</td>}
                  <td className={`${tableDataCellClass} text-right tabular-nums ${v.errored ? 'text-danger' : ''}`}>{v.errored ? `${compact(v.errored)} · ${pct(v.error_rate)}` : '—'}</td>
                  <td className={`${tableDataCellClass} whitespace-nowrap text-right tabular-nums`}>{v.latency_ms.p50 == null ? '—' : `${duration(v.latency_ms.p50)} · ${duration(v.latency_ms.p95)}`}</td>
                  <td className={`${tableDataCellClass} text-right tabular-nums`}>{v.cost_per_verdict_usd ? currency(v.cost_per_verdict_usd) : '—'}</td>
                  {anyPeople && <td className={`${tableDataCellClass} text-right tabular-nums`} title={`${v.marked_right} right · ${v.marked_wrong} wrong`}>{v.agreement == null ? '—' : `${pct(v.agreement)} of ${v.marked_right + v.marked_wrong}`}</td>}
                  {anyAudit && <td className={`${tableDataCellClass} text-right tabular-nums`}>{v.audited ? `${v.audit_disagreements} of ${v.audited}` : '—'}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {shownKeys.length > 0 && lines.rows.length > 1 && (
        <Card title="Pass rate by version" info="Each version's pass rate per bucket, over its resolved verdicts. A line never bridges a version change."
          controls={(
            <span className="flex flex-wrap items-center gap-3 text-[11px] text-[color:var(--text-muted)]">
              {shownKeys.map((k) => (
                <span key={k} className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ background: colorOf[Number(k.slice(1))] || TOKEN.brand }} />{k}</span>
              ))}
            </span>
          )}>
          <Chart height={200} summary={`Pass rate over time for ${shownKeys.join(', ')}`}>
            {({ width, height }) => (
              <ComposedChart width={width} height={height} data={lines.rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: 'var(--border-default)' }} tickLine={false} minTickGap={16} />
                <YAxis hide domain={[0, 1]} />
                <Tooltip {...hoverProps} content={(p) => <HoverCard {...p} rows={(r) => shownKeys.map((k) => (r[k] != null ? { name: k, color: colorOf[Number(k.slice(1))] || TOKEN.brand, value: pct(r[k]) } : null))} />} />
                {shownKeys.map((k) => (
                  <Line key={k} dataKey={k} name={k} type="monotone" stroke={colorOf[Number(k.slice(1))] || TOKEN.brand} strokeWidth={1.5}
                    dot={lines.rows.length > 14 ? false : { r: 2, strokeWidth: 0 }} activeDot={{ r: 3 }} connectNulls={false} isAnimationActive={false} />
                ))}
              </ComposedChart>
            )}
          </Chart>
        </Card>
      )}

      {pairs.length > 0 && (
        <Card title="Head to head" info="Units two versions both scored — a re-score or a backfill — and how often they agreed. The flips are what changed.">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] table-fixed text-left text-[12px]" aria-label="Head to head">
              <thead>
                <tr className={tableHeaderRowClass}>
                  <th scope="col" className={th()}>Versions</th>
                  <th scope="col" className={th('text-right')}>Both decided</th>
                  <th scope="col" className={th('text-right')}>Agreed</th>
                  <th scope="col" className={th('text-right')}>Pass → fail</th>
                  <th scope="col" className={th('text-right')}>Fail → pass</th>
                </tr>
              </thead>
              <tbody>
                {pairs.map((h) => (
                  <tr key={`${h.from_version}-${h.to_version}`} className={readRow}>
                    <td className={`${tableDataCellClass} tabular-nums`}>v{h.from_version} <span className="text-[color:var(--text-muted)]">→</span> v{h.to_version}</td>
                    <td className={`${tableDataCellClass} text-right tabular-nums`}>{compact(h.both_decided)}</td>
                    <td className={`${tableDataCellClass} text-right tabular-nums`}>{pct(h.agreement)}</td>
                    <td className={`${tableDataCellClass} text-right tabular-nums`}>{compact(h.pass_to_fail)}</td>
                    <td className={`${tableDataCellClass} text-right tabular-nums`}>{compact(h.fail_to_pass)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

/**
 * @param {object} props.stats  getStats for the same window — totals (the results page already has it)
 */
export default function Eval3AnalysisPanel({ teamId, lineageId, days = 30, stats: pageStats = null, through: controlled, onThroughChange, version = null, onVersionChange = null }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sla, setSla] = useState(null);
  // The page's Filter owns the scope when it passes one; alone, the panel keeps its own.
  const [own, setOwn] = useState([]);
  const through = controlled || own;
  const setThrough = (next) => {
    const v = typeof next === 'function' ? next(through) : next;
    if (onThroughChange) onThroughChange(v); else setOwn(v);
  };
  const [steps, setSteps] = useState(null);
  // The viewer's clock: series come per hour of it, and the chart clusters on it.
  const tz = useMemo(() => viewerTz(), []);
  const [picked, setPicked] = useState(null);       // the cluster size someone chose; unset is automatic
  const [scopedStats, setScopedStats] = useState(null);
  const [compared, setCompared] = useState(null);
  const [view, setView] = useState('overview');     // overview | versions
  const key = through.join(',');

  useEffect(() => {
    let cancelled = false;
    setData(null);
    getTelemetry(teamId, lineageId, days, through, tz, version)
      .then((d) => { if (!cancelled) { setData(d); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message); });
    return () => { cancelled = true; };
  }, [teamId, lineageId, days, key, version]); // eslint-disable-line react-hooks/exhaustive-deps

  // Every version side by side — never narrowed by the version picked, which it is for choosing.
  useEffect(() => {
    let cancelled = false;
    Promise.resolve(compareVersions(teamId, lineageId, days, through))
      .then((d) => { if (!cancelled) setCompared(d || null); })
      .catch(() => { if (!cancelled) setCompared(null); });
    return () => { cancelled = true; };
  }, [teamId, lineageId, days, key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Paths and per-step numbers, in the same scope.
  useEffect(() => {
    let cancelled = false;
    new Promise((resolve) => resolve(getSteps(teamId, lineageId, days, through, tz, version)))
      .then((d) => { if (!cancelled && d) setSteps(d); })
      .catch(() => { if (!cancelled) setSteps(null); });
    return () => { cancelled = true; };
  }, [teamId, lineageId, days, key, version]); // eslint-disable-line react-hooks/exhaustive-deps

  // The tiles' totals: the page's own when it passes the scope, fetched for the scope otherwise.
  useEffect(() => {
    let cancelled = false;
    if (!through.length || controlled) { setScopedStats(null); return undefined; }
    new Promise((resolve) => resolve(getStats(teamId, lineageId, days, through, version)))
      .then((d) => { if (!cancelled) setScopedStats(d || null); })
      .catch(() => { if (!cancelled) setScopedStats(null); });
    return () => { cancelled = true; };
  }, [teamId, lineageId, days, key, version]); // eslint-disable-line react-hooks/exhaustive-deps
  const stats = through.length && !controlled ? scopedStats : pageStats;
  const toggle = (id) => setThrough((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  useEffect(() => {
    let cancelled = false;
    Promise.resolve(getSla(teamId, lineageId, days))
      .then((s) => { if (!cancelled) setSla(s || null); })
      .catch(() => { if (!cancelled) setSla(null); });
    return () => { cancelled = true; };
  }, [teamId, lineageId, days]);

  const shaped = useMemo(() => {
    if (!data) return null;
    const rel = data.reliability;
    const nowKey = hourKeyAt(new Date(), tz);
    const at = rel.series.length ? clusterFor({ keys: rel.series.map((r) => r.day), days, nowKey, picked }) : null;
    const label = (r) => ({ ...r, label: at ? bucketLabel(r.day, at.size) : dayLabel(r.day) });
    return {
      rel, eff: data.efficiency, at,
      rows: statesByDay(rel.series, at).map(label).map((r) => ({
        ...r, rate: r.passed + r.failed ? r.passed / (r.passed + r.failed) : null,
      })),
      seams: seamDays(rel.seams, at, tz).map(label),
    };
  }, [data, picked, days, tz]);

  if (error) return <div className="m-4 rounded-md border border-[color:var(--feedback-error)] bg-danger-soft p-3 text-[12px] text-danger" role="alert">{error}</div>;
  if (!shaped) return <PageLoader label="Loading analysis" />;

  const { rel, eff, at, rows, seams } = shaped;
  // Versions is its own view once there are two to compare.
  const comparable = (compared?.versions || []).filter((v) => v.results > 0).length > 1;
  const viewSwitch = comparable ? (
    <SegmentedTabs size="sm" ariaLabel="Analysis view" value={view} onChange={setView}
      options={[{ value: 'overview', label: 'Overview' }, { value: 'versions', label: 'Versions' }]} />
  ) : null;
  if (comparable && view === 'versions') {
    return (
      <div className="space-y-3 p-4" data-testid="eval3-analysis">
        {viewSwitch}
        <VersionsCompare compared={compared} series={rel.series} at={at}
          onPick={onVersionChange ? (v) => { onVersionChange(v); setView('overview'); } : null} />
      </div>
    );
  }
  const scores = rel.scores && rel.scores.scored > 0 ? rel.scores : null;
  const anyAvg = rows.some((r) => r.avg_score != null);
  const s = rel.states || {};
  const resolved = (s.passed || 0) + (s.failed || 0);
  const passRate = resolved ? s.passed / resolved : null;
  const t = stats?.totals;
  const base = baselineOf(compared?.versions, version);
  const delta = base && points(passRate, base.pass_rate) ? `${points(passRate, base.pass_rate)} vs v${base.version}` : null;
  // The average of whatever this eval scores — a person's 1-5 rating, or a judge's number. Most evals carry none.
  const avgScore = t && t.scored > 0 && t.avg_score != null ? t.avg_score : null;
  const scoreRange = avgScore != null && t.score_min != null && t.score_max != null && t.score_min !== t.score_max
    ? `${scoreFmt(t.score_min)}–${scoreFmt(t.score_max)}` : null;
  const scoredLine = avgScore != null ? `${compact(t.scored)} of ${compact(resolved)} ${resolved === 1 ? 'verdict' : 'verdicts'} scored` : null;

  const present = STATES.filter((st) => rows.some((r) => r[st.key] > 0));
  const anyRate = rows.some((r) => r.rate != null);

  // Where runs end: the picked version's, else the newest that has any.
  const exitVersions = eff.path_distribution.map((v) => v.version).sort((a, b) => b - a);
  const shownExit = exitVersions.includes(version) ? version : exitVersions[0];
  const exits = eff.path_distribution.find((v) => v.version === shownExit);
  const showExits = exits && exits.exits.length > 0;

  const latencyName = (l) => (eff.latency.filter((x) => KIND_LABEL(x.instrument) === KIND_LABEL(l.instrument)).length > 1 ? `${KIND_LABEL(l.instrument)} · ${l.instrument}` : KIND_LABEL(l.instrument));
  const maxP95 = Math.max(0, ...eff.latency.map((l) => l.p95 || l.p50 || 0));
  const chosenSteps = through.map((id) => (steps?.steps || []).find((st) => st.node_id === id)).filter(Boolean);
  const rulings = sla?.rulings ? sla.rulings.screen_right + sla.rulings.screen_wrong : 0;

  return (
    <div className="space-y-3 p-4" data-testid="eval3-analysis">
      {viewSwitch}
      {through.length > 0 && (
        <div className="flex items-center gap-2 text-[11.5px] text-[color:var(--text-muted)]" data-testid="analysis-scope">
          <span data-testid="scope-count">
            {steps ? `${compact(steps.runs_matched)} ${steps.runs_matched === 1 ? 'run' : 'runs'} went through ${through.length === 1 ? 'the chosen step' : `all ${through.length} chosen steps`}` : ''}
          </span>
          <button type="button" onClick={() => setThrough([])} className="rounded px-1.5 py-0.5 hover:bg-surface-hover hover:text-[color:var(--text-primary)]">Show every run</button>
        </div>
      )}

      {version != null && (
        <div className="flex items-center gap-2 text-[11.5px] text-[color:var(--text-muted)]" data-testid="analysis-version">
          <span>Only what v{version} scored{base ? `, compared with v${base.version}` : ''}.</span>
          {onVersionChange && <button type="button" onClick={() => onVersionChange(null)} className="rounded px-1.5 py-0.5 hover:bg-surface-hover hover:text-[color:var(--text-primary)]">Show every version</button>}
        </div>
      )}

      <dl className={`grid grid-cols-2 gap-3 ${avgScore != null ? 'md:grid-cols-5' : 'md:grid-cols-4'}`} aria-label="Summary">
        <Tile label={version != null ? `Pass rate · v${version}` : 'Pass rate'} value={passRate == null ? '—' : pct(passRate)}
          sub={delta} hover={resolved ? `${s.passed} of ${resolved} verdicts` : undefined} />
        {avgScore != null && (
          <Tile label="Average score" value={scoreFmt(avgScore)} sub={scoredLine}
            hover={[scoredLine, scoreRange ? `scores ran ${scoreRange}` : null].filter(Boolean).join(' · ')} />
        )}
        <Tile label="Verdicts" value={compact(resolved)} sub={s.pending ? `${compact(s.pending)} waiting` : null} />
        <Tile label="Errors" value={compact(s.errored || 0)} tone={s.errored ? 'warn' : undefined} />
        <Tile label="Cost" value={t ? currency(t.provider_cost_usd) : '—'} hover={t ? `${compact(t.units)} units` : undefined} />
      </dl>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        {rows.length > 0 && (
          <Card title="Verdicts" testId="card-verdicts" className="xl:col-span-2"
            info={anyRate ? 'Passed, failed and errored per bucket; the thin line is the pass rate of resolved verdicts.' : undefined}
            controls={(
              <>
                <span className="flex items-center gap-3 text-[11px] text-[color:var(--text-muted)]">
                  {present.map((st) => (
                    <span key={st.key} className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ background: st.color }} />{st.label}</span>
                  ))}
                </span>
                <ClusterBy title="Verdicts" cluster={at} onChange={setPicked} />
              </>
            )}>
            <Chart height={220} summary={`Verdicts over time: ${present.map((st) => `${st.label.toLowerCase()} ${s[st.key] || 0}`).join(', ')}`}>
              {({ width, height }) => (
                <ComposedChart width={width} height={height} data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                  <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: 'var(--border-default)' }} tickLine={false} minTickGap={16} />
                  <YAxis yAxisId="n" hide />
                  <YAxis yAxisId="r" hide domain={[0, 1]} />
                  <Tooltip {...hoverProps} content={(p) => (
                    <HoverCard {...p} rows={(r) => [
                      ...present.map((st) => (r[st.key] ? { name: st.label, color: st.color, value: compact(r[st.key]) } : null)),
                      r.rate != null ? { name: 'Pass rate', color: TOKEN.brand, value: pct(r.rate) } : null,
                    ]} />
                  )} />
                  {seams.map((sm) => (
                    <ReferenceLine key={sm.version} yAxisId="n" x={sm.label} stroke="var(--text-disabled)" strokeDasharray="2 3"
                      label={{ value: `v${sm.version}`, fontSize: 10, fill: 'var(--text-muted)', position: 'insideTopLeft' }} />
                  ))}
                  {present.map((st) => (
                    <Bar key={st.key} yAxisId="n" dataKey={st.key} name={st.label} stackId="s" fill={st.color} fillOpacity={0.85}
                      radius={st.key === present[present.length - 1].key ? [2, 2, 0, 0] : 0}
                      maxBarSize={28} isAnimationActive={false} />
                  ))}
                  {anyRate && (
                    <Line yAxisId="r" dataKey="rate" name="Pass rate" type="monotone" stroke={TOKEN.brand} strokeWidth={1.5}
                      dot={rows.length > 14 ? false : { r: 2, strokeWidth: 0, fill: TOKEN.brand }} activeDot={{ r: 3 }} connectNulls isAnimationActive={false} />
                  )}
                </ComposedChart>
              )}
            </Chart>
          </Card>
        )}

        {scores && (
          <Card title="Scores" testId="card-scores" className="xl:col-span-2"
            info="The score this eval gives, over the scored verdicts: how it spread (each bar split by how those runs ended) and its average per bucket.">
            <div className={`grid grid-cols-1 gap-6 ${anyAvg && rows.length > 1 ? 'lg:grid-cols-2' : ''}`}>
              <div className="min-w-0">
                <div className="mb-1 flex items-baseline justify-between text-[11px] text-[color:var(--text-muted)]">
                  <span>Spread</span><span className="tabular-nums">mean {scoreFmt(scores.mean)} · {compact(scores.scored)} scored</span>
                </div>
                <Histogram dist={scores.distribution} height={160} summary={`Scores from ${scores.distribution.min} to ${scores.distribution.max}, mean ${scores.mean}`} />
              </div>
              {anyAvg && rows.length > 1 && (
                <div className="min-w-0">
                  <div className="mb-1 text-[11px] text-[color:var(--text-muted)]">Average over time</div>
                  <Chart height={160} summary="Average score over time">
                    {({ width, height }) => (
                      <ComposedChart width={width} height={height} data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                        <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: 'var(--border-default)' }} tickLine={false} minTickGap={16} />
                        <YAxis hide domain={['auto', 'auto']} />
                        <Tooltip {...hoverProps} content={(p) => <HoverCard {...p} rows={(r) => [
                          r.avg_score != null ? { name: 'Average', color: TOKEN.brand, value: scoreFmt(r.avg_score) } : null,
                          r.scored ? { name: 'Scored', color: 'var(--text-muted)', value: compact(r.scored) } : null,
                        ]} />} />
                        {seams.map((sm) => (
                          <ReferenceLine key={sm.version} x={sm.label} stroke="var(--text-disabled)" strokeDasharray="2 3"
                            label={{ value: `v${sm.version}`, fontSize: 10, fill: 'var(--text-muted)', position: 'insideTopLeft' }} />
                        ))}
                        <Line dataKey="avg_score" type="monotone" stroke={TOKEN.brand} strokeWidth={1.5} connectNulls
                          dot={rows.length > 14 ? false : { r: 2, strokeWidth: 0, fill: TOKEN.brand }} activeDot={{ r: 3 }} isAnimationActive={false} />
                      </ComposedChart>
                    )}
                  </Chart>
                </div>
              )}
            </div>
          </Card>
        )}

        {compared && <VersionsCard data={compared} selected={version} onPick={onVersionChange} />}

        {showExits && (
          <Card title={exitVersions.length > 1 ? `Where runs end · v${shownExit}` : 'Where runs end'} testId="card-exits"
            info="The step whose decision ended each run, and the verdict it led to. A run that ends at a cheap step never reached the judge.">
            <ul className="space-y-2.5 pt-1" aria-label={`Where runs end, v${exits.version}`}>
              {exits.exits.map((e) => (
                <BarRow key={`${e.exit_node}-${e.verdict_node}`} fraction={e.share} color={outcomeTone(e.passed)}
                  outcome={e.passed === false ? 'fail' : e.passed === true ? 'pass' : 'unknown'}
                  title={`${e.exit_node} → ${e.verdict_node}`}
                  label={<>{e.exit_name || e.exit_node} <span className="text-[color:var(--text-muted)]">→</span> {e.verdict_name || e.verdict_node}</>}
                  figure={<>{pct(e.share)} <span className="text-[color:var(--text-muted)]">· {compact(e.runs)}</span></>} />
              ))}
            </ul>
          </Card>
        )}

        {eff.latency.length > 0 && (
          <Card title="Step latency" info="Median, with the 95th percentile as the lighter bar." testId="card-latency">
            <ul className="space-y-2.5 pt-1" data-testid="eval3-latency">
              {eff.latency.map((l, i) => (
                <li key={l.instrument} className="text-[12px]" title={`${compact(l.calls)} calls · p99 ${l.p99 == null ? '—' : duration(l.p99)}`}>
                  <div className="mb-1 flex items-baseline justify-between gap-3">
                    <span className="min-w-0 truncate text-[color:var(--text-primary)]">{latencyName(l)}</span>
                    <span className="shrink-0 tabular-nums text-[color:var(--text-secondary)]">
                      {l.p50 == null ? '—' : duration(l.p50)} <span className="text-[color:var(--text-muted)]">· {l.p95 == null ? '—' : duration(l.p95)}</span>
                    </span>
                  </div>
                  <div className="relative h-1.5 overflow-hidden rounded-full" aria-hidden data-row={i}>
                    <div className="absolute inset-y-0 left-0 rounded-full opacity-30" style={{ width: `${maxP95 ? Math.max(1, ((l.p95 || l.p50 || 0) / maxP95) * 100) : 0}%`, background: TOKEN.brand }} />
                    <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${maxP95 ? Math.max(1, ((l.p50 || 0) / maxP95) * 100) : 0}%`, background: TOKEN.brand }} />
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {chosenSteps.map((st) => <StepCard key={st.node_id} step={st} onRemove={() => toggle(st.node_id)} />)}
        {/* An eval of one step is that step: its card is the analysis, with no need to pick it first. */}
        {!chosenSteps.length && steps?.steps?.length === 1 && <StepCard key={steps.steps[0].node_id} step={steps.steps[0]} onRemove={null} />}
        {/* One path, or one step, has nothing to choose between. */}
        {steps && (steps.paths || []).length > 1 && <PathsCard paths={steps.paths} runs={steps.runs_matched} onPick={setThrough} />}
        {steps && (steps.steps || []).length > 1 && <StepsCard steps={steps.steps} chosen={through} onToggle={toggle} />}

        {/* Only once there is something to show: an audit sample nobody has run is not a number. */}
        {rel.screen_error.measured && (
          <Card title="Screen accuracy" info="From the audit sample: how often a screen's shortcut disagreed with where the full path would have gone." testId="card-screens">
            <div className="flex items-baseline gap-2 pb-2" data-testid="eval3-screen-error">
              <span className="text-[24px] font-semibold tabular-nums text-[color:var(--text-primary)]">{pct(rel.screen_error.rate)}</span>
              <span className="text-[11px] text-[color:var(--text-muted)]">
                disagreed · {rel.screen_error.disagreements} of {rel.screen_error.audited - rel.screen_error.unresolved} audited
                {sla && sla.measurable > 0 ? ` · 95% ${pct(sla.screen_error.ci95.low)}–${pct(sla.screen_error.ci95.high)}` : ''}
                {rulings > 0 ? ` · after ${rulings} ${rulings === 1 ? 'ruling' : 'rulings'}: ${pct(sla.refined_screen_error.rate)}` : ''}
              </span>
            </div>
            {sla?.by_screen?.length > 0 && (
              <table className="w-full text-left text-[12px]" data-testid="eval3-by-screen">
                <thead>
                  <tr className={tableHeaderRowClass}>
                    <th scope="col" className={th()}>Screen</th>
                    <th scope="col" className={th('text-right')}>Disagreed</th>
                    <th scope="col" className={th('text-right')}>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {sla.by_screen.map((sc) => (
                    <tr key={sc.screen} className={readRow}>
                      <td className={`${tableDataCellClass} font-mono`}>{sc.screen}</td>
                      <td className={`${tableDataCellClass} text-right tabular-nums`}>{sc.disagreements} of {sc.shadowed}</td>
                      <td className={`${tableDataCellClass} text-right tabular-nums`}>{pct(sc.rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
